import { RegistrationApiService, NavigationValidationResponse } from '../../../features/registration/api/RegistrationApiService';
import { Result } from '../../../shared/api/services/result';

// Registration steps and sections as defined by the backend
export enum RegistrationStep {
  SCAN_ID = 'scan_id',
  FACE_LIVE = 'face_live',
  DOCTOR_INFO = 'doctor_info',
  DOCTOR_DOCUMENTS = 'doctor_documents',
  CHOOSE_DOCTOR_POINT = 'choose_doctor_point',
  CLINIC_INFO = 'clinic_info',
  UPLOADING_DOCS = 'uploading_docs',
  AGREE_TERMS = 'agree_terms',
  PAY = 'pay',
  COMPLETED = 'completed'
}

export enum RegistrationSection {
  LOGIN_OTP = 'login_otp',
  ID_FACE = 'id_face',
  PROFILE = 'profile'
}

export interface NavigationValidationResult {
  can_navigate: boolean;
  current_step: string;
  current_section: RegistrationSection;
  target_section: RegistrationSection;
  error_message: string | null;
  will_reset_progress: boolean;
  reset_to_step: string | null;
}

export interface NavigationError {
  type: 'SECTION_BLOCKED' | 'FORWARD_SKIP' | 'PERMISSION_DENIED';
  message: string;
  currentStep: string;
  targetStep: string;
}

export class NavigationService {
  private registrationApi: RegistrationApiService;

  constructor() {
    this.registrationApi = new RegistrationApiService();
  }

  /**
   * Get the section for a registration step
   */
  getRegistrationSection(step: string): RegistrationSection {
    const sectionMap: Record<string, RegistrationSection> = {
      // Section 2: ID & Face & Doctor Info
      [RegistrationStep.SCAN_ID]: RegistrationSection.ID_FACE,
      [RegistrationStep.FACE_LIVE]: RegistrationSection.ID_FACE,
      [RegistrationStep.DOCTOR_INFO]: RegistrationSection.ID_FACE,
      [RegistrationStep.DOCTOR_DOCUMENTS]: RegistrationSection.ID_FACE,

      // Section 3: Profile
      [RegistrationStep.CHOOSE_DOCTOR_POINT]: RegistrationSection.PROFILE,
      [RegistrationStep.CLINIC_INFO]: RegistrationSection.PROFILE,
      [RegistrationStep.UPLOADING_DOCS]: RegistrationSection.PROFILE,
      [RegistrationStep.AGREE_TERMS]: RegistrationSection.PROFILE,
      [RegistrationStep.PAY]: RegistrationSection.PROFILE,
      [RegistrationStep.COMPLETED]: RegistrationSection.PROFILE,
    };

    return sectionMap[step] || RegistrationSection.LOGIN_OTP;
  }

  /**
   * Validate navigation to a target step
   */
  async validateNavigation(targetStep: string): Promise<Result<NavigationValidationResult>> {
    const validationResult = await this.registrationApi.validateNavigation(targetStep);
    
    if (!validationResult.isSuccess) {
      return Result.failure(validationResult.error || 'Navigation validation failed');
    }

    return Result.success({
      can_navigate: validationResult.data!.can_navigate,
      current_step: validationResult.data!.current_step,
      current_section: validationResult.data!.current_section as RegistrationSection,
      target_section: validationResult.data!.target_section as RegistrationSection,
      error_message: validationResult.data!.error_message,
      will_reset_progress: validationResult.data!.will_reset_progress,
      reset_to_step: validationResult.data!.reset_to_step
    });
  }

  /**
   * Check if navigation is allowed and handle doctor confirmation for progress reset
   */
  async canNavigateToWithConfirmation(
    targetStep: string,
    confirmationCallback?: (message: string) => Promise<boolean>
  ): Promise<{ canNavigate: boolean; error?: string }> {
    try {
      const validation = await this.validateNavigation(targetStep);
      
      if (!validation.isSuccess) {
        return { canNavigate: false, error: validation.error };
      }

      const result = validation.data!;

      if (!result.can_navigate) {
        return { 
          canNavigate: false, 
          error: result.error_message || 'Navigation not allowed' 
        };
      }

      // If navigation will reset progress, ask for doctor confirmation
      if (result.will_reset_progress && confirmationCallback) {
        const message = `Editing ${targetStep} will reset your progress to ${result.reset_to_step}. You'll need to complete the steps after ${targetStep} again. Continue?`;
        const confirmed = await confirmationCallback(message);
        
        if (!confirmed) {
          return { canNavigate: false, error: 'Navigation cancelled by doctor' };
        }
      }

      return { canNavigate: true };
    } catch (error) {
      return { 
        canNavigate: false, 
        error: error instanceof Error ? error.message : 'Navigation validation failed' 
      };
    }
  }

  /**
   * Get doctor-friendly error message for navigation restrictions
   */
  getNavigationErrorMessage(
    currentStep: string,
    targetStep: string,
    errorType: 'SECTION_BLOCKED' | 'FORWARD_SKIP' | 'PERMISSION_DENIED'
  ): string {
    const currentSection = this.getRegistrationSection(currentStep);
    const targetSection = this.getRegistrationSection(targetStep);

    switch (errorType) {
      case 'SECTION_BLOCKED':
        if (currentSection === RegistrationSection.ID_FACE && targetSection === RegistrationSection.LOGIN_OTP) {
          return 'You cannot return to phone/OTP verification once you\'ve started ID verification. Please complete the current section.';
        } else if (currentSection === RegistrationSection.PROFILE) {
          return 'You cannot return to previous sections once you\'ve started profile completion. Please complete the current section.';
        }
        return 'You cannot return to previous sections once completed.';
        
      case 'FORWARD_SKIP':
        return 'Please complete your current step before proceeding to the next one.';
        
      case 'PERMISSION_DENIED':
        return 'You do not have permission to access this step at this time.';
        
      default:
        return 'Navigation is not allowed at this time.';
    }
  }

  /**
   * Get the path mapping for registration steps
   */
  getStepRoute(step: string): string {
    const routeMap: Record<string, string> = {
      [RegistrationStep.SCAN_ID]: '/scan_id',
      [RegistrationStep.FACE_LIVE]: '/face_live',
      [RegistrationStep.DOCTOR_INFO]: '/doctor_info_registration',
      [RegistrationStep.DOCTOR_DOCUMENTS]: '/doctor_documents',
      [RegistrationStep.CHOOSE_DOCTOR_POINT]: '/choose_doctor_procedure',
      [RegistrationStep.CLINIC_INFO]: '/clinic_info_registration',
      [RegistrationStep.UPLOADING_DOCS]: '/document_upload',
      [RegistrationStep.AGREE_TERMS]: '/agree_terms',
      [RegistrationStep.PAY]: '/payment',
      [RegistrationStep.COMPLETED]: '/success'
    };

    return routeMap[step] || '/scan_id';
  }

  /**
   * Get the registration step from a route path
   */
  getStepFromRoute(path: string): string {
    const stepMap: Record<string, string> = {
      '/scan_id': RegistrationStep.SCAN_ID,
      '/face_live': RegistrationStep.FACE_LIVE,
      '/doctor_info_registration': RegistrationStep.DOCTOR_INFO,
      '/doctor_documents': RegistrationStep.DOCTOR_DOCUMENTS,
      '/choose_doctor_procedure': RegistrationStep.CHOOSE_DOCTOR_POINT,
      '/clinic_info_registration': RegistrationStep.CLINIC_INFO,
      '/document_upload': RegistrationStep.UPLOADING_DOCS,
      '/agree_terms': RegistrationStep.AGREE_TERMS,
      '/payment': RegistrationStep.PAY,
      '/success': RegistrationStep.COMPLETED
    };

    return stepMap[path] || RegistrationStep.SCAN_ID;
  }

  /**
   * Get all steps in order for a section
   */
  getSectionSteps(section: RegistrationSection): string[] {
    switch (section) {
      case RegistrationSection.ID_FACE:
        return [
          RegistrationStep.SCAN_ID,
          RegistrationStep.FACE_LIVE,
          RegistrationStep.DOCTOR_INFO,
          RegistrationStep.DOCTOR_DOCUMENTS
        ];
      case RegistrationSection.PROFILE:
        return [
          RegistrationStep.CHOOSE_DOCTOR_POINT,
          RegistrationStep.CLINIC_INFO,
          RegistrationStep.UPLOADING_DOCS,
          RegistrationStep.AGREE_TERMS,
          RegistrationStep.PAY,
          RegistrationStep.COMPLETED
        ];
      default:
        return [];
    }
  }

  /**
   * Check if a step is within a specific section
   */
  isStepInSection(step: string, section: RegistrationSection): boolean {
    return this.getSectionSteps(section).includes(step);
  }

  /**
   * Get the next step in registration flow, considering verification status
   * @param currentStep The current registration step
   * @param hasFullName Whether doctor has full_name set (scanned ID)
   * @param hasFacePhoto Whether face_verification_photo exists
   * @returns The next step to navigate to
   */
  getNextStepWithVerificationCheck(
    currentStep: string,
    hasFullName: boolean = false,
    hasFacePhoto: boolean = false
  ): string {
    // If at scan_id and full_name exists, skip to face_live or doctor_info
    if (currentStep === RegistrationStep.SCAN_ID && hasFullName) {
      // If face photo also exists, skip both verification steps
      if (hasFacePhoto) {
        return RegistrationStep.DOCTOR_INFO;
      }
      // Only ID scanned (has full_name), go to face verification
      return RegistrationStep.FACE_LIVE;
    }

    // If at face_live and face photo already uploaded, skip to doctor_info
    if (currentStep === RegistrationStep.FACE_LIVE && hasFacePhoto) {
      return RegistrationStep.DOCTOR_INFO;
    }

    // Default: return the current step (no skip)
    return currentStep;
  }

  /**
   * Resolve the effective step a doctor should be on, considering verification flags
   * This is used when navigating after login or refresh to skip already-verified steps
   */
  resolveEffectiveStep(
    registrationProgress: string,
    hasFullName: boolean = false,
    hasFacePhoto: boolean = false
  ): string {
    // If progress is scan_id but full_name exists (ID already scanned), advance
    if (registrationProgress === RegistrationStep.SCAN_ID && hasFullName) {
      if (hasFacePhoto) {
        return RegistrationStep.DOCTOR_INFO;
      }
      return RegistrationStep.FACE_LIVE;
    }

    // If progress is face_live but face photo uploaded, advance
    if (registrationProgress === RegistrationStep.FACE_LIVE && hasFacePhoto) {
      return RegistrationStep.DOCTOR_INFO;
    }

    // No skip needed
    return registrationProgress;
  }
}

// Singleton instance
export const navigationService = new NavigationService(); 